import React, { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { CreditCard, Landmark, Copy, Check } from "lucide-react";
import { usd, itemKey, personKey, CARD_PAYMENTS_ENABLED } from "../../lib/api";

const card = "rounded-2xl bg-white border border-[#E2EBF0] p-6 shadow-lg shadow-emerald-950/5";

export function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <div><div className="text-xs text-slate-500">{label}</div><div className="font-semibold text-[#03281B] font-mono text-sm">{value}</div></div>
      <button onClick={copy} className="shrink-0 rounded-lg border border-[#E2EBF0] p-2 hover:bg-[#F2F8F5] text-[#008057]">{copied ? <Check size={16} /> : <Copy size={16} />}</button>
    </div>
  );
}

export const OrderDetails = ({ quote }) => (
  <div className={card}>
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-3">Order Details</div>
    <div className="text-sm text-slate-600 mb-3">Category: <strong className="text-[#03281B]">{quote.category.name}</strong></div>
    {quote.participants.map((p) => (
      <div key={personKey(p)} className="border-t border-slate-100 py-2">
        <div className="font-semibold text-[#03281B]">{p.name}</div>
        {p.items.map((it) => (
          <div key={itemKey(it)} className="flex justify-between text-sm text-slate-600"><span>{it.label}</span><span className="font-mono">{usd(it.amount)}</span></div>
        ))}
      </div>
    ))}
    <div className="flex justify-between font-display font-extrabold text-2xl text-[#03281B] border-t-2 border-[#008057] pt-3 mt-2"><span>Total Due</span><span data-testid="checkout-total">{usd(quote.total)}</span></div>
  </div>
);

const MethodButton = ({ testId, active, onClick, icon: Icon, title, sub }) => (
  <button data-testid={testId} onClick={onClick} className={`rounded-xl border-2 p-4 text-left transition-all ${active ? "border-[#008057] bg-[#F2F8F5]" : "border-[#E2EBF0]"}`}>
    <Icon className="text-[#008057]" /><div className="font-bold text-[#03281B] mt-2">{title}</div><div className="text-xs text-slate-500">{sub}</div>
  </button>
);

const ZelleBox = ({ zelle }) => (
  <div className="mt-4 rounded-xl border border-[#008057]/25 bg-white p-3">
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Zelle / Chase QuickPay <span className="text-[#F3C242]">· fastest</span></div>
    <div className="flex items-center gap-4">
      <div className="bg-white p-2 rounded-lg border border-slate-200 shrink-0"><QRCodeCanvas data-testid="checkout-zelle-qr" value={zelle.handle} size={94} fgColor="#03281B" /></div>
      <div className="min-w-0 flex-1">
        <CopyRow label="Zelle number" value={zelle.display || zelle.handle} />
        {zelle.name && <div className="text-xs text-slate-500 mt-1">Recipient: {zelle.name}</div>}
        <p className="text-[11px] text-slate-400 mt-1">Scan in your banking app or send Zelle to this number, then add your reference in the memo.</p>
      </div>
    </div>
  </div>
);

const BankDetails = ({ bank }) => (
  <div className="mt-5 rounded-xl bg-[#F2F8F5] p-4">
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Bank Transfer Details</div>
    <CopyRow label="Bank" value={bank.bank} />
    <CopyRow label="Account holder" value={bank.account_holder} />
    <CopyRow label="Account number" value={bank.account_number} />
    <CopyRow label="Routing (ACH / direct deposit)" value={bank.routing_ach} />
    <CopyRow label="Routing (wire transfers)" value={bank.routing_wire} />
    <div className="text-xs text-slate-500 mt-2">Account holder's address: {(bank.address_lines || []).join(", ")}</div>
    {bank.zelle?.handle && <ZelleBox zelle={bank.zelle} />}
    <p className="text-xs text-slate-500 mt-3">A unique registration reference will be generated. Please include it in your payment reference. Your registration will be created as <strong>Pending Payment</strong> until we verify funds.</p>
  </div>
);

export const PaymentMethod = ({ method, setMethod, bank }) => (
  <div className={card}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-4">Payment Method</div>
    <div className={`grid ${CARD_PAYMENTS_ENABLED ? "sm:grid-cols-2" : "grid-cols-1"} gap-3`}>
      {CARD_PAYMENTS_ENABLED && <MethodButton testId="payment-method-stripe" active={method === "stripe"} onClick={() => setMethod("stripe")} icon={CreditCard} title="Credit Card" sub="Secure payment via Stripe" />}
      <MethodButton testId="payment-method-bank" active={method === "bank"} onClick={() => setMethod("bank")} icon={Landmark} title="Bank Transfer" sub="Pay by ACH or wire" />
    </div>
    {method === "bank" && <BankDetails bank={bank} />}
  </div>
);

export const CampSidebar = ({ count, quote }) => (
  <div className="rounded-2xl bg-[#03281B] text-white p-6 lg:sticky lg:top-20">
    <div className="font-display font-bold text-lg">Sporting CP Camp San Diego 2027</div>
    <div className="text-white/60 text-sm mt-1">Point Loma Nazarene University</div>
    <div className="text-white/60 text-sm">June 9-13, 2027</div>
    <div className="mt-4 pt-4 border-t border-white/10 text-sm space-y-1">
      <div className="flex justify-between"><span className="text-white/60">Participants</span><span>{count}</span></div>
      <div className="flex justify-between"><span className="text-white/60">Category</span><span>{quote.category.name}</span></div>
      <div className="flex justify-between font-bold text-[#F3C242] text-lg mt-2"><span>Total</span><span>{usd(quote.total)}</span></div>
    </div>
  </div>
);
